import { DataProvider } from "react-admin";
import qs from "qs";
import { HttpError } from "react-admin";

const API_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3001/api";
const OKTA_LOCAL_STORAGE_KEY =
  process.env.REACT_APP_OKTA_LOCAL_STORAGE_KEY || "okta-token-storage";

// Add Okta token to request
const httpClient = (
  url: string,
  options: { method?: string; body?: string | FormData; headers?: any } = {}
) => {
  const tokenStorage = localStorage.getItem(OKTA_LOCAL_STORAGE_KEY);
  let accessToken;
  if (tokenStorage) {
    try {
      const tokenObj = JSON.parse(tokenStorage);
      accessToken = tokenObj.accessToken?.accessToken;
    } catch (e) {
      // ignore
    }
  }
  options.headers = options.headers || {};
  if (accessToken) {
    options.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (
    options.body &&
    typeof options.body !== "string" &&
    !(options.body instanceof FormData)
  ) {
    options.body = JSON.stringify(options.body);
    options.headers["Content-Type"] = "application/json";
  } else if (typeof options.body === "string") {
    options.headers["Content-Type"] = "application/json";
  } else if (options.body instanceof FormData) {
    // Let browser set Content-Type for FormData
    delete options.headers["Content-Type"];
  }
  return fetch(url, options).then(async (response) => {
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new HttpError(
        json?.error || "Unknown error",
        response.status,
        json
      );
    }
    return { status: response.status, headers: response.headers, json };
  });
};

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
    const { field, order } = params.sort || { field: "", order: "" };
    const filter = params.filter || {};
    if (filter.id) {
      filter._id = filter.id;
      filter.id = undefined;
    }
    const query = {
      sort: field && order ? [[field, order]] : [],
      limit: perPage,
      page: page,
      filter,
    };
    const searchKey = Object.keys(filter).find((key) => key.startsWith("q_"));
    if (searchKey) {
      filter.$or = searchKey
        .replace("q_", "")
        .split("_")
        .map((searchField) => {
          return {
            [searchField]: { $regex: filter[searchKey], $options: "i" },
          };
        });
      filter[searchKey] = undefined;
    }

    const searchListKey = Object.keys(filter).find((key) =>
      key.startsWith("ql_")
    );
    if (searchListKey) {
      // split by comma or whitespace
      filter[searchListKey.replace("ql_", "")] = {
        $in: filter[searchListKey].split(/[,\s.]+/),
      };
      filter[searchListKey] = undefined;
    }

    const url = `${API_URL}/${resource}?${qs.stringify(query)}`;
    const { json } = await httpClient(url);

    return {
      data: json.data.map((resource: { _id: string }) => ({
        ...resource,
        id: resource._id,
      })),
      total: json?.pagination?.totalResults || 0,
      hasNextPage: json?.pagination?.hasNextPage || false,
    };
  },

  getUnique: async (resource: string, params: object) =>
    httpClient(`${API_URL}/${resource}/unique?${qs.stringify(params)}`).then(
      ({ json }) => json.data.sort((a: string, b: string) => b.localeCompare(a))
    ),

  getChart: async (resource: string, params: object) =>
    httpClient(`${API_URL}/${resource}?${qs.stringify(params)}`).then(
      ({ json }) => json.data
    ),

  getOne: (resource, params) =>
    httpClient(`${API_URL}/${resource}/${params.id}`).then(({ json }) => ({
      data: { ...json.data, id: json.data._id },
    })),

  getMany: async (resource, params) => {
    // NOTE: The limit is hardcoded to the upper limit supported by the backend.
    // getMany doesn't natively support pagination.
    // This should be sufficient for smaller ReferenceArrayField components (like in release show page)
    const query = {
      limit: 100,
      filter: { _id: { $in: params.ids } },
    };
    const url = `${API_URL}/${resource}?${qs.stringify(query)}`;
    const { json } = await httpClient(url);
    return {
      data: json.data.map((resource: { _id: string }) => ({
        ...resource,
        id: resource._id,
      })),
    };
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
    const { field, order } = params.sort || { field: "", order: "" };
    const filter = params.filter || {};

    const query = {
      sort: field && order ? [[field, order]] : [],
      limit: perPage,
      page: page,
      filter: {
        ...filter,
        [params.target === "id" ? "_id" : params.target]: params.id,
      },
    };
    const url = `${API_URL}/${resource}?${qs.stringify(query)}`;

    const { json } = await httpClient(url);
    return {
      data: json.data.map((resource: { _id: string }) => ({
        ...resource,
        id: resource._id,
      })),
      total: json.pagination.totalResults || 0,
    };
  },

  update: (resource, params) =>
    httpClient(`${API_URL}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...json.data, id: json.data._id },
    })),

  updateMany: async (resource, params) =>
    Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${resource}/${encodeURIComponent(id)}`, {
          method: "PUT",
          body: JSON.stringify(params.data),
        })
      )
    ).then((responses) => ({
      data: responses.map(({ json }) => json.id),
    })),

  create: async (resource, params) => {
    let options;
    if (resource === "cve/sboms") {
      const formData = new FormData();
      formData.append("name", params.data.name);
      formData.append("description", params.data.description);
      formData.append("release", params.data.release);
      if (params.data.uploadedFile && params.data.uploadedFile.rawFile) {
        formData.append(
          "uploadedFile",
          params.data.uploadedFile.rawFile,
          params.data.uploadedFile.title
        );
      }
      options = {
        method: "POST",
        body: formData,
        // Do NOT set Content-Type, browser will set it
      };
    } else {
      options = {
        method: "POST",
        body: JSON.stringify(params.data),
        headers: { "Content-Type": "application/json" },
      };
    }
    const { json } = await httpClient(`${API_URL}/${resource}`, options);
    return { data: { ...json.data, id: json.data._id } };
  },

  delete: (resource, params) =>
    httpClient(`${API_URL}/${resource}/${params.id}`, {
      method: "DELETE",
    }).then(({ json }) => ({ data: { ...json.data, id: json.data._id } })),

  deleteMany: async (resource, params) =>
    Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${resource}/${encodeURIComponent(id)}`, {
          method: "DELETE",
        })
      )
    ).then((responses) => ({
      data: responses.map(({ json }) => json.id),
    })),

  // Fetch all items for a resource by paging through all results
  getAll: async (resource: string | undefined, params = {}) => {
    if (!resource) {
      throw new Error("Resource is required");
    }
    let page = 1;
    const perPage = 1000;
    let allData: any[] = [];
    let fetchNextPage = true;
    let total = 0;
    do {
      const { data, totalResults, hasNextPage } = await (
        dataProvider as any
      ).getList(resource, {
        ...params,
        pagination: { page, perPage },
      });
      page++;
      allData = allData.concat(data);
      fetchNextPage = hasNextPage;
      total = totalResults;
    } while (fetchNextPage);
    return { data: allData, total };
  },
  custom: async (urlPath: string, method: string, body: any) => {
    const { json } = await httpClient(`${API_URL}/${urlPath}`, {
      method,
      body,
    });
    return { data: json.data };
  },
  addMitigation: async (riskId: string, formData: FormData) => {
    const { json } = await httpClient(
      `${API_URL}/rmf/risks/${riskId}/mitigations`,
      {
        method: "POST",
        body: formData,
      }
    );
    return { data: json.data };
  },
  launchEndorContainerScan: async (imageUri: string) => {
    const { json } = await httpClient(
      `${API_URL}/cve/endor-container-scans/launch`,
      { method: "POST", body: JSON.stringify({ imageUri }) }
    );
    return { data: json };
  },
  getEndorContainerScanStatus: async (sidequestJobId: number) => {
    const { json } = await httpClient(
      `${API_URL}/cve/endor-container-scans/status/${sidequestJobId}`
    );
    return { data: json };
  },
  getEndorContainerScanFindings: async (
    projectUuid: string,
    namespace: string
  ) => {
    const { json } = await httpClient(
      `${API_URL}/cve/endor-container-scans/findings/${encodeURIComponent(
        projectUuid
      )}?namespace=${encodeURIComponent(namespace)}`
    );
    return { data: json.data ?? [] };
  },
  savePreRelease: async (
    name: string,
    description: string,
    entries: Array<{
      imageUri: string;
      projectUuid?: string;
      projectName?: string;
      findings: unknown[];
    }>
  ) => {
    const { json } = await httpClient(`${API_URL}/cve/pre-releases`, {
      method: "POST",
      body: JSON.stringify({ name, description, entries }),
    });
    return { data: json.data };
  },
  rescanPreRelease: async (preReleaseId: string) => {
    const { json } = await httpClient(
      `${API_URL}/cve/pre-releases/${encodeURIComponent(preReleaseId)}/rescan`,
      { method: "POST" }
    );
    return { data: json.data };
  },
  comparePreReleaseToRelease: async (
    preReleaseId: string,
    releaseId: string
  ) => {
    const { json } = await httpClient(
      `${API_URL}/cve/pre-releases/${encodeURIComponent(
        preReleaseId
      )}/compare?releaseId=${encodeURIComponent(releaseId)}`
    );
    return { data: json.data };
  },
};
